import type { LocationQuery, RouteRecordRaw } from "vue-router"
import type { RouteNamedMap } from "vue-router/auto-routes"

/**
 * Name of the route param that carries the vanity slug when a route is
 * matched via its `/:slug?` prefixed alias (see `addSlugAliases`).
 */
export const SLUG_PARAM_NAME = "slug"

/**
 * Route names that should NOT get a `/:slug?` prefixed alias.
 *
 * These are routes that either aren't tied to a specific event (account/
 * system pages, listings across all events) or are otherwise not relevant
 * in a slug context.
 */
export const SLUG_EXCLUDED_ROUTE_NAMES: ReadonlySet<keyof RouteNamedMap> = new Set([
  // Auth / system pages
  "/login",
  "/signup",
  "/[partnerSlug]/signup",
  "/callback",
  "/silent-renew",
  "/verify-email",
  "/lockout",
  "/access-restricted",
  "/no-access",
  "/403",
  "/[...path]",

  // Account-level pages (not scoped to a single event)
  "/profile",
  "/guest/events",
  "/onboard/setup",
  "/onboard/purchase-confirmation",
  "/event/create",
  "/event/dashboard",

  // Invite-token flows — arrive via external links with a token and should
  // not be slug-aliased as they are account-level onboarding, not event browsing.
  "/event/[id]/accept-invite",

  // Templates (host-account-level, not tied to an event's slug)
  "/template/create",
  "/template/[id]/edit",
  "/template/[id]/assign",
])

function toAliasArray(alias: RouteRecordRaw["alias"]): string[] {
  if (!alias)
    return []

  return Array.isArray(alias) ? alias : [alias]
}

/**
 * Resolves the absolute path of a route given its parent's already-resolved
 * absolute path. File-based routing nests routes to mirror the folder
 * structure: parent segments (e.g. the `event` folder, or the `[id]`
 * dynamic segment) are unnamed container records with a *relative* `path`,
 * while only leaf `.vue` files end up with a `name`. Segments already
 * starting with `/` (this is how every top-level record's path looks) reset
 * to an absolute path rather than being appended.
 */
function resolveAbsolutePath(parentPath: string, segmentPath: string): string {
  if (segmentPath.startsWith("/"))
    return segmentPath

  if (!segmentPath)
    return parentPath

  return parentPath.endsWith("/") ? `${parentPath}${segmentPath}` : `${parentPath}/${segmentPath}`
}

/**
 * Mutates `routes` in place, adding a `/:slug?` prefixed alias to every route
 * whose name isn't in `SLUG_EXCLUDED_ROUTE_NAMES`. This allows every
 * supported route to also be reached as `/:slug?${originalAbsolutePath}`
 * (e.g. `/event/:id/edit` -> also `/:slug?/event/:id/edit`) while keeping
 * `route.name` and `route.meta` identical between the canonical path and its
 * slug-prefixed alias.
 *
 * The `slug` param is declared optional (`:slug?`) rather than required.
 * Functionally it's always populated when the alias path is actually used,
 * but marking it optional also avoids a vue-router dev-only warning that
 * would otherwise fire because the alias introduces a param the canonical
 * route doesn't have (vue-router's alias/param-parity check only complains
 * about *required* params that don't match).
 *
 * Routes generated by file-based routing are nested (mirroring the
 * `src/pages` folder structure) rather than flattened, so this walks the
 * whole tree recursively, tracking each route's resolved absolute path as it
 * goes, and only adds aliases to routes that actually have a `name` (leaf
 * page routes) — the intermediate folder/param segments are left untouched.
 */
export function addSlugAliases(routes: RouteRecordRaw[], parentPath = ""): void {
  for (const route of routes) {
    const absolutePath = resolveAbsolutePath(parentPath, route.path)

    if (
      typeof route.name === "string"
      && !SLUG_EXCLUDED_ROUTE_NAMES.has(route.name as keyof RouteNamedMap)
    ) {
      route.alias = [
        ...toAliasArray(route.alias),
        `/:${SLUG_PARAM_NAME}?${absolutePath}`,
      ]
    }

    if (route.children?.length) {
      addSlugAliases(route.children, absolutePath)
    }
  }
}

/**
 * A minimal route-location-like shape, satisfied by both `useRoute()`'s
 * current route / a `router.resolve()` result (used by `AppLink`) and a
 * `router.beforeEach` guard's `to`/`from` (`RouteLocationNormalized`, used
 * by the slug-persistence guard in `router.ts`).
 *
 * `name` is deliberately typed as a broad `string | symbol` rather than
 * vue-router's `RouteRecordName` — this app's `vue-router/auto-routes` typed
 * routes plugin augments `RouteRecordName` to `keyof RouteNamedMap | symbol`,
 * which would force every literal passed in here (including in tests) to be
 * narrowed to a *known* route name, rather than just checked at runtime via
 * `typeof to.name === "string"` as this function actually does.
 *
 * `path`/`query`/`hash` are kept as separate fields (rather than a single
 * `fullPath` string) so the slug-prefixed result can also keep them
 * separate — see the note on `SlugPersistedPath` below.
 */
interface SlugPersistenceLocation {
  name?: string | symbol | null
  params?: Record<string, string | string[] | undefined>
  path: string
  query?: LocationQuery
  hash?: string
}

/**
 * The slug-prefixed redirect target, returned as `path`/`query`/`hash`
 * fields rather than a single concatenated string.
 *
 * This matters: vue-router does NOT re-parse a `?query` string embedded in
 * a `{ path }` navigation target the way it does for a plain string passed
 * to `router.push()` — when a `beforeEach` guard returns `{ path: "/foo?bar=1" }`,
 * the `?bar=1` is treated as a literal (and effectively dropped) part of the
 * path rather than becoming `to.query.bar`. Keeping `query`/`hash` as their
 * own fields avoids that pitfall for both callers (the `beforeEach` guard's
 * redirect object and `AppLink`'s `:to` prop, which vue-router's
 * `RouterLink`/`router.resolve` both handle correctly as separate fields).
 */
export interface SlugPersistedPath {
  path: string
  query?: LocationQuery
  hash?: string
}

/**
 * Given the current (`from`) and a target (`to`) route location, returns the
 * slug-prefixed location that should be navigated to instead, or `undefined`
 * if the target should be left unchanged.
 *
 * This is the single source of truth for whether/how a vanity slug should
 * carry across navigation, shared by `AppLink` (for `<RouterLink>` clicks)
 * and the `beforeEach` guard in `router.ts` (for imperative
 * `router.push`/`router.replace` calls) so the two can't drift apart.
 */
export function resolveSlugPersistedPath(
  from: Pick<SlugPersistenceLocation, "params">,
  to: SlugPersistenceLocation,
): SlugPersistedPath | undefined {
  const slug = from.params?.[SLUG_PARAM_NAME]
  if (typeof slug !== "string" || !slug)
    return undefined

  if (typeof to.name !== "string" || SLUG_EXCLUDED_ROUTE_NAMES.has(to.name as keyof RouteNamedMap))
    return undefined

  const targetParams = to.params ?? {}

  // The target already has its own explicit slug segment — don't double-prefix.
  if (targetParams[SLUG_PARAM_NAME])
    return undefined

  // A slug belongs to one specific event. If the current route and the
  // target route both carry an `id` param and they differ, this navigation
  // points at a different event, so carrying the current slug across would
  // be wrong — leave it unprefixed.
  const normaliseId = (v: string | string[] | undefined) => Array.isArray(v) ? v[0] : v
  const fromId = normaliseId(from.params?.id)
  const toId = normaliseId(targetParams.id)
  if (fromId && toId && fromId !== toId)
    return undefined

  return { path: `/${slug}${to.path}`, query: to.query, hash: to.hash || undefined }
}
